import { useState } from 'react';
import axios from 'axios';
import { addDays, differenceInYears, format, parse } from 'date-fns';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { formatDateStringIfMatches, parseDate } from '../Utils/stringUtils';
import { genderMapping } from '../Constants/gender';
import differenceInCalendarMonths from 'date-fns/esm/fp/differenceInCalendarMonths/index';
import { isNotNullOrUndefined } from '../Utils/genericUtils';
import { enableFrontdoor } from '../Utils/features';

const BASE_URL = enableFrontdoor ? process.env.REACT_APP_OCE_API_BASE_URL_FRONTDOOR : process.env.REACT_APP_OCE_API_BASE_URL;

export default function useOceApi() {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const executeRequest = (request) => {
        setLoading(true);
        setSuccess(false);
        setError(null);
        setResult(null);

        const fromDateNumeric = parseDate(request.fromDate, DATE_STRING_FORMAT);
        const toDateNumeric = parseDate(request.toDate, DATE_STRING_FORMAT);
        const proxyProcessDate = toDateNumeric ? addDays(toDateNumeric,1) : fromDateNumeric ? addDays(fromDateNumeric,1) : new Date();
        const claimID = !request.claimId ? null : request.claimId.length > 17 ? request.claimId.substring(0, 17) : request.claimId;
        axios.post(`${BASE_URL}/oce`, { 
            claimID,
            age: isNotNullOrUndefined(request.birthDate) ? differenceInYears(new Date(), parse(request.birthDate, DATE_STRING_FORMAT, new Date())) :  null,
            sex: genderMapping[request.gender] || null,
            fromDate: fromDateNumeric && format(fromDateNumeric, API_DATE_STRING_FORMAT),
            toDate: toDateNumeric && format(toDateNumeric, API_DATE_STRING_FORMAT),
            billType: request.billType || null,
            npi: request.npi || null,
            medicareID: request.medicareId || null,
            conditionCodes: request.conditionCodes && request.conditionCodes.filter(cc => cc.code).map(cc => cc.code),
            occurenceCodes: request.occurrenceCodes && request.occurrenceCodes.filter(oc => oc.code).map(oc => oc.code),
            valueCodes: request.valueCodes && request.valueCodes.filter(vc => vc.code).map(vc => {
                const amount = Number(vc.amount);
                return {
                    code: vc.code,
                    amount: isNaN(amount) ? null : amount 
                }
            }),
            dischargedStatus: (!isNaN(request.dischargedStatus) && request.dischargedStatus !== null && Number(request.dischargedStatus)) || null,
            claimsProcessingReceiptDate: format(proxyProcessDate, API_DATE_STRING_FORMAT),
            principalDiagnosisCode: (request.diagnosisCodes && request.diagnosisCodes.length >= 1 && request.diagnosisCodes[0].diagnosisCode) || null,
            secondaryDiagnosisCodes: request.diagnosisCodes && request.diagnosisCodes.filter((dc, index) => dc.diagnosisCode && index !== 0).map(dc => dc.diagnosisCode),
            lines: (request.lines && request.lines.filter(l => l.billed).map(l => {
                const modifiers = [];
                if (l.modifierOne) {
                    modifiers.push(l.modifierOne);
                }

                if (l.modifierTwo) {
                    modifiers.push(l.modifierTwo);
                }
                return {
                    lineID: l.lineId || null,
                    procedureCode: l.procedureCode || null,
                    modifiers: modifiers,
                    serviceDate: formatDateStringIfMatches(l.serviceDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT) || null,
                    revenueCode: l.revenueCode || null,
                    units: !isNaN(l.units) && l.units !== null && l.units?.toString()?.trim() !== '' ? Number(l.units) : null,
                    billed: !isNaN(l.billed) && l.billed !== null && l.billed?.toString()?.trim() !== '' ? Number(l.billed) : null
                };
            })) || null
        })
        .then(({ data }) => {
            setResult(data);
            setSuccess(true);
        }).catch(e => {
            setError(e);
        }).finally(() => {
            setLoading(false);
        });
    };

    const executeReset = () => {
        setLoading(false);
        setSuccess(false);
        setError(null);
        setResult(null);
    }

    return {
        loading, 
        success, 
        error, 
        result, 
        execute: executeRequest, 
        reset: executeReset
    };
}